import { useEffect } from 'react';
import { useLanguage } from '../store/LanguageContext';
import { API_URL } from '../lib/api';

interface ReviewItem {
  id: string;
  author_name: string;
  rating: number;
  title: string | null;
  body: string;
  created_at: number;
}

/**
 * Injects a JSON-LD AggregateRating + Review schema into <head> based on
 * approved reviews from /api/reviews/schema. Combined with the LocalBusiness
 * schema in SEOHead, this produces Rich Results (star snippet) in Google.
 *
 * Mounted once at app root level (App.tsx). Re-fetches on language switch
 * so reviews in the visitor's language take priority.
 */
export default function DynamicSchema() {
  const { lang } = useLanguage();

  useEffect(() => {
    const SCHEMA_ID = 'data-jsonld-reviews';

    let aborted = false;
    const inject = async () => {
      try {
        const res = await fetch(`${API_URL}/api/reviews/schema?lang=${lang}`);
        if (!res.ok) return;
        const json = await res.json() as { ok: boolean; data: { count: number; average: number; reviews: ReviewItem[] } };
        if (aborted || !json.ok || json.data.count === 0) return;

        // Remove old node
        const old = document.querySelector(`script[${SCHEMA_ID}]`);
        if (old) old.remove();

        const orgWithReviews = {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': 'https://gevelsteenbedrijf.be/#organization-reviews',
          name: 'Gevelsteenbedrijf.be',
          url: 'https://gevelsteenbedrijf.be',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: json.data.average,
            reviewCount: json.data.count,
            bestRating: 5,
            worstRating: 1,
          },
          review: json.data.reviews.slice(0, 5).map((r) => ({
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
            author: { '@type': 'Person', name: r.author_name },
            datePublished: new Date(r.created_at * 1000).toISOString().split('T')[0],
            reviewBody: r.body,
            ...(r.title ? { name: r.title } : {}),
          })),
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute(SCHEMA_ID, '');
        script.textContent = JSON.stringify(orgWithReviews);
        document.head.appendChild(script);
      } catch {
        /* network failure — fallback static schema in index.html still serves */
      }
    };

    void inject();
    return () => {
      aborted = true;
      const old = document.querySelector(`script[${SCHEMA_ID}]`);
      if (old) old.remove();
    };
  }, [lang]);

  return null;
}
